import { useCallback, useEffect, useState } from "react";
import { useListThreadMessages, useThread } from "duck/endpoints/threads";
import { ChatMessage } from "duck/ui/chat/types";
import { extractChatMessages } from "duck/ui/chat/utils";
import { mutate } from "swr";

interface UseThreadStreamingProps {
  threadId: string;
  skipRequest?: boolean;
}

/**
 * A simpler hook for streaming messages to a specific thread.
 * Unlike useStreamingChat, this doesn't manage thread creation, followups, or global state.
 * It's designed for subthreads that just need to display streaming messages.
 * Also fetches thread metadata (title) and provides mutateThread for refetching.
 */
export const useThreadStreaming = ({
  threadId,
  skipRequest = false,
}: UseThreadStreamingProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // fetch thread metadata (includes title)
  const { data: threadData, requestKey: threadRequestKey } = useThread(
    {
      threadId,
      skipRequest,
    },
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );

  // fetch messages from API for this thread
  const { data: threadMessagesData } = useListThreadMessages(
    {
      threadId,
      skipRequest,
    },
    {
      refreshInterval: 0, // don't auto-refresh, we handle updates via SSE
      revalidateOnFocus: false,
    }
  );

  // sync messages from API when not streaming
  // this handles both initial load (refresh) and post-streaming refetch (for server IDs)
  useEffect(() => {
    if (threadMessagesData) {
      const chatMessages = extractChatMessages(threadMessagesData);
      if (chatMessages.length > 0) {
        setMessages(chatMessages);
      }
    }
  }, [threadMessagesData]);

  const mutateThread = useCallback(() => {
    if (threadRequestKey) {
      mutate(threadRequestKey);
    }
  }, [threadRequestKey]);

  return {
    messages,
    setMessages,
    title: threadData?.title ?? null,
    mutateThread,
  };
};
